import { useMemo } from 'react';
import { sortByKey } from '../businessLogicService/sortCriteria';

type ChartValue = {
  label: 'NOT_ANSWERED' | 'NOT_PASSED' | 'PASSED';
  color: string;
  value: number;
  className: string;
  users: ExitSlipUser[];
};

type ChartItem = {
  questionNumber: string | null;
  itemId: string | null;
  students: number;
  values: ChartValue[];
};

type ExitSlipItem = {
  id: string;
  score: number;
  maxScore: number;
  attempted: boolean;
  itemReference: string;
  questionNumber: string | null;
};

type UserClass = {
  createdOn: string;
  user: {
    id: string;
    lastName: string | null | undefined;
    fullName: string | null | undefined;
    publicUserId: string;
    roles: {
      id: string;
      name: string | null | undefined;
    }[];
  };
};

export interface ExitSlipUser {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  fullName: string | null | undefined;
  publicUserId: string;
}

export type LearnosityExitSlipSession = {
  id: string;
  activityId: string;
  isComplete: boolean;
  maxScore: number;
  publicUserId: string;
  score: number;
  sessionId: string;
  sessionCompletedOn?: string | null;
  newResponse: boolean;
  user: ExitSlipUser;
  items: ExitSlipItem[];
};

export type ExitSlipClass = {
  id: string;
  name: string | null | undefined;
  userClasses: UserClass[];
};

interface TargetGroup {
  name: string | null | undefined;
}

interface Skill {
  code: string | null | undefined;
  id: string;
  name: string | null | undefined;
}

interface CurriculumLesson {
  id: string | null | undefined;
  number: string | null | undefined;
  name: string | null | undefined;
  targetGroup: TargetGroup;
  skill: Skill;
}

export type ClassExitSlip = {
  id: string;
  canceledOn: string | null | undefined;
  curriculumLesson: CurriculumLesson;
  learnosityExitSlipSessions: LearnosityExitSlipSession[];
  class: ExitSlipClass[];
};

export interface LessonData {
  id: string;
  dueOn: string;
  canceledOn?: string | null;
  curriculumLesson: {
    name: string;
    number: string;
  };
  learnosityExitSlipSessions?: LearnosityExitSlipSession[];
}

export interface FullDetailModalData {
  visible: boolean;
  title: string;
}

export interface LearnosityReportData {
  activityId: string;
  userId: string;
  reportId: string;
  reportType: string;
  userIds: { id: string; name: string }[];
  sessionIds: string[];
}

const STATUS_CONFIG: Omit<ChartValue, 'value'>[] = [
  {
    label: 'NOT_ANSWERED',
    color: '#E1E1E1',
    className: 'nc_exit-slip-meter-not-answered',
    users: [],
  },
  {
    label: 'NOT_PASSED',
    color: '#B71C1B',
    className: 'nc_exit-slip-meter-not-passed',
    users: [],
  },
  {
    label: 'PASSED',
    color: '#008158',
    className: 'nc_exit-slip-meter-passed',
    users: [],
  },
];

export function useExitSlipChartData(classExitSlips: ClassExitSlip): ChartItem[] {
  return useMemo(() => {
    const questionMap: Record<
      string,
      {
        questionNumber: string | null;
        itemId: string | null;
        NOT_ANSWERED: { count: number; users: ExitSlipUser[] };
        NOT_PASSED: { count: number; users: ExitSlipUser[] };
        PASSED: { count: number; users: ExitSlipUser[] };
      }
    > = {};

    let totalStudents = 0;
    const sessions = classExitSlips.learnosityExitSlipSessions || [];
    totalStudents += sessions.length;

    for (const session of sessions) {
      const user = session.user;
      for (const item of session.items) {
        const ref = item.questionNumber ? String(item.questionNumber) : item.itemReference;

        if (!questionMap[ref]) {
          questionMap[ref] = {
            NOT_ANSWERED: { count: 0, users: [] },
            NOT_PASSED: { count: 0, users: [] },
            PASSED: { count: 0, users: [] },
            questionNumber: null,
            itemId: null,
          };
        }

        if (!item.attempted) {
          questionMap[ref].NOT_ANSWERED.count++;
          questionMap[ref].NOT_ANSWERED.users.push(user);
        } else if (item.score < item.maxScore) {
          questionMap[ref].NOT_PASSED.count++;
          questionMap[ref].NOT_PASSED.users.push(user);
        } else {
          questionMap[ref].PASSED.count++;
          questionMap[ref].PASSED.users.push(user);
        }
        questionMap[ref].questionNumber = item.questionNumber ? String(item.questionNumber) : null;
        questionMap[ref].itemId = item.id ? item.id : null;
      }
    }

    const chartItems = Object.entries(questionMap).map(([, counts]) => {
      const statusValues: ChartValue[] = STATUS_CONFIG.map(config => ({
        label: config.label,
        color: config.color,
        value: counts[config.label].count,
        className: config.className,
        users: counts[config.label].users.sort((a, b) =>
          a.fullName!.localeCompare(b.fullName!, undefined, { sensitivity: 'base' }),
        ),
      }));

      return {
        questionNumber: counts.questionNumber,
        itemId: counts.itemId,
        students: totalStudents,
        values: statusValues,
      };
    });

    //for old exit slips without questionNumber, sort by itemId
    if (chartItems.length) {
      if (chartItems[0].questionNumber !== null) {
        chartItems.sort(sortByKey('questionNumber'));
      } else {
        chartItems.sort(sortByKey('itemId'));
      }
    }
    return chartItems;
  }, [classExitSlips]);
}
